import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppLayout from "@/components/layout/AppLayout";
import api from "@/lib/api";
import { Bell, AlertTriangle, Clock, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/format";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const TYPE_META = {
  "15_days": { label: "15 days", tone: "yellow", icon: Clock },
  "7_days": { label: "7 days", tone: "yellow", icon: Clock },
  "3_days": { label: "3 days", tone: "orange", icon: Clock },
  "1_day": { label: "1 day", tone: "orange", icon: Clock },
  "on_deadline": { label: "Today", tone: "red", icon: Bell },
  "overdue": { label: "Overdue", tone: "red", icon: AlertTriangle },
};

const TONE = {
  yellow: "bg-yellow-50 text-yellow-800 border-yellow-200",
  orange: "bg-orange-50 text-orange-800 border-orange-200",
  red: "bg-red-50 text-red-800 border-red-200",
};

export default function Reminders() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/reminders", { params: { unread_only: showUnreadOnly } });
      setItems(data);
    } finally { setLoading(false); }
  }, [showUnreadOnly]);

  useEffect(() => { load(); }, [load]);

  const markAllRead = async () => {
    await api.post("/reminders/read-all");
    toast.success("All reminders marked as read");
    load();
  };

  const markOne = async (r) => {
    await api.post(`/reminders/${r.id}/read`);
    load();
  };

  const openCompliance = async (r) => {
    await markOne(r);
    nav(`/compliances?open=${r.compliance_id}`);
  };

  const renderBody = () => {
    if (loading) return <div className="p-8 text-slate-500 text-sm">Loading…</div>;
    if (items.length === 0) return (
      <div className="p-16 text-center">
        <Bell className="w-8 h-8 mx-auto text-slate-300" />
        <div className="mt-3 text-sm text-slate-500">No reminders right now. You're all caught up.</div>
      </div>
    );
    return items.map((r) => {
      const meta = TYPE_META[r.reminder_type] || TYPE_META["7_days"];
      const Icon = meta.icon;
      return (
        <div
          key={r.id}
          onClick={() => openCompliance(r)}
          className={cn("flex items-start gap-4 px-6 py-4 cursor-pointer hover:bg-slate-50 transition-colors", !r.read && "bg-blue-50/40")}
          data-testid={`reminder-${r.id}`}
        >
          <div className={cn("w-9 h-9 rounded-md flex items-center justify-center border shrink-0", TONE[meta.tone])}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className={cn("text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded border font-semibold", TONE[meta.tone])}>{meta.label}</span>
              {!r.read && <span className="w-1.5 h-1.5 rounded-full bg-blue-600" />}
              <span className="text-xs text-slate-500 font-mono-num">{formatDate(r.date)}</span>
            </div>
            <div className="mt-1 text-sm text-slate-900 font-medium">{r.message}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {r.client_name} · {r.compliance_type} · Deadline {formatDate(r.deadline)}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <AppLayout
      title="Reminders"
      actions={
        <>
          <button
            onClick={() => setShowUnreadOnly((v) => !v)}
            className={cn("px-3 py-1.5 rounded-md text-xs font-medium border transition-colors",
              showUnreadOnly ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 text-slate-700")}
            data-testid="filter-unread"
          >
            Unread only
          </button>
          <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-btn">
            <CheckCheck className="w-4 h-4 mr-1.5" /> Mark all read
          </Button>
        </>
      }
    >
      <div className="bg-white border border-slate-200 rounded-xl divide-y divide-slate-200">
        {renderBody()}
      </div>
    </AppLayout>
  );
}
